import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { instituicaoService, cidadeService } from '@/services/api';
import { Instituicao, Cidade, PaginationResponse } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

const instituicaoSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  nomeFantasia: z.string().min(1, 'Nome fantasia é obrigatório'),
  cidadeId: z.number().min(1, 'Cidade é obrigatória'),
  cnpj: z.string().optional(),
  endereco: z.string().optional(),
  telefone: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  nomeDiretor: z.string().optional(),
  cpfDiretor: z.string().optional(),
});

type InstituicaoFormData = Omit<Instituicao, 'id'>;

export const NovaInstituicaoPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateInstituicoes } = useQueryInvalidation();
  const [loadError, setLoadError] = useState<string | null>(null);

  // Estados para o select com busca de cidade
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [cidadeQuery, setCidadeQuery] = useState<string>("");
  const [openCidade, setOpenCidade] = useState<boolean>(false);
  const cidadeRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<InstituicaoFormData>({
    resolver: zodResolver(instituicaoSchema),
  });

  // Buscar dados da instituição (se editando)
  const { data: instituicaoData, isLoading: loadingInstituicao, error: instituicaoError } = useQuery<Instituicao>({
    queryKey: ['instituicao', id],
    queryFn: () => instituicaoService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (instituicaoError) {
      const message = handleError(instituicaoError as any, 'Erro ao carregar instituição');
      setLoadError(message);
    }
  }, [instituicaoError, handleError]);

  useEffect(() => {
    if (instituicaoData) {
      setLoadError(null);
    }
  }, [instituicaoData]);

  // Buscar cidades para o select
  const { data: cidadesData, isLoading: loadingCidades, error: cidadesError } = useQuery<PaginationResponse<Cidade>>({
    queryKey: ['cidades'],
    queryFn: () => cidadeService.list({ page: 1, limit: 1000 }),
  });

  useEffect(() => {
    if (cidadesError) {
      handleError(cidadesError as any, 'Erro ao carregar cidades');
    }
  }, [cidadesError, handleError]);

  // Carregar dados das cidades para o select com busca
  useEffect(() => {
    if (cidadesData?.data) {
      setCidades(cidadesData.data);
    }
  }, [cidadesData]);

  // Filtro para o select de cidade
  const filteredCidades = cidades.filter(c =>
    !cidadeQuery || c.nome.toLowerCase().includes(cidadeQuery.toLowerCase()) || c.uf.toLowerCase().includes(cidadeQuery.toLowerCase())
  );

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: any) {
      const target = event.target;
      if (cidadeRef.current && target && !cidadeRef.current.contains(target)) {
        setOpenCidade(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const createInstituicaoMutation = useMutation({
    mutationFn: instituicaoService.create,
    onSuccess: () => {
      invalidateInstituicoes();
      handleSuccess('Instituição criada com sucesso!');
      navigate('/instituicoes');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao criar instituição');
    },
  });

  const updateInstituicaoMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: InstituicaoFormData }) =>
      instituicaoService.update(id, data),
    onSuccess: () => {
      invalidateInstituicoes(id);
      handleSuccess('Instituição atualizada com sucesso!');
      navigate('/instituicoes');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao atualizar instituição');
    },
  });

  // Preencher formulário com dados existentes (se editando)
  useEffect(() => {
    if (instituicaoData) {
      setValue('nome', instituicaoData.nome);
      setValue('nomeFantasia', instituicaoData.nomeFantasia || '');
      setValue('cnpj', instituicaoData.cnpj || '');
      setValue('endereco', instituicaoData.endereco || '');
      setValue('cidadeId', instituicaoData.cidadeId);
      setValue('telefone', instituicaoData.telefone || '');
      setValue('email', instituicaoData.email || '');
      setValue('nomeDiretor', instituicaoData.nomeDiretor || '');
      setValue('cpfDiretor', instituicaoData.cpfDiretor || '');

      // Definir valor inicial para o select de cidade com busca
      if (instituicaoData.cidade) {
        setCidadeQuery(`${instituicaoData.cidade.nome} - ${instituicaoData.cidade.uf}`);
      }
    }
  }, [instituicaoData, setValue]);

  const onSubmit = async (data: InstituicaoFormData) => {
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await updateInstituicaoMutation.mutateAsync({ id: Number(id), data });
      } else {
        await createInstituicaoMutation.mutateAsync(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state para edição ou carregamento de cidades
  if (isEditing && loadError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Instituição</h1>
          <p className="mt-1 text-sm text-gray-500">Não foi possível carregar as informações da instituição.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Erro ao carregar dados</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/instituicoes')}>Voltar para lista</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if ((isEditing && loadingInstituicao) || loadingCidades) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">
            {isEditing ? 'Carregando dados da instituição...' : 'Carregando cidades...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Instituição' : 'Nova Instituição'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing 
            ? 'Edite as informações da instituição' 
            : 'Cadastre uma nova instituição de ensino no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados da Instituição</CardTitle>
            <CardDescription>
              Informações básicas da instituição
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome da Instituição *</Label>
              <Input
                id="nome"
                placeholder="Ex: Universidade Federal de São Carlos"
                {...register('nome')}
                className={errors.nome ? 'border-error-500' : ''}
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-error-600">{errors.nome.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="nomeFantasia">Nome Fantasia *</Label>
              <Input
                id="nomeFantasia"
                placeholder="Ex: UFSCar"
                {...register('nomeFantasia')}
                className={errors.nomeFantasia ? 'border-error-500' : ''}
              />
              {errors.nomeFantasia && (
                <p className="mt-1 text-sm text-error-600">{errors.nomeFantasia.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="cidadeId">Cidade *</Label>
              <div className="relative" ref={cidadeRef}>
                <input
                  type="text"
                  placeholder="Selecione a cidade"
                  className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.cidadeId ? 'border-error-500' : 'border-gray-300'
                  }`}
                  value={cidadeQuery}
                  onChange={(e) => { 
                    setCidadeQuery(e.target.value); 
                    setOpenCidade(true); 
                  }}
                  onFocus={() => setOpenCidade(true)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setOpenCidade(false); }}
                  disabled={loadingCidades}
                />
                {cidadeQuery && (
                  <button
                    type="button"
                    aria-label="Limpar cidade"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => { 
                      setCidadeQuery(""); 
                      setValue('cidadeId', 0);
                    }}
                  >
                    ×
                  </button>
                )}
                {openCidade && (
                  <div
                    className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="px-2 py-1 text-xs text-gray-500">Cidades</div>
                    {filteredCidades.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-100"
                        onClick={() => { 
                          setValue('cidadeId', c.id);
                          setCidadeQuery(`${c.nome} - ${c.uf}`);
                          setOpenCidade(false); 
                        }}
                      >
                        {c.nome} - {c.uf}
                      </button>
                    ))}
                    {filteredCidades.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">Nenhuma cidade encontrada</div>
                    )}
                  </div>
                )}
              </div>
              {errors.cidadeId && (
                <p className="mt-1 text-sm text-error-600">{errors.cidadeId.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input
                id="cnpj"
                placeholder="Ex: 12.345.678/0001-90"
                {...register('cnpj')}
                className={errors.cnpj ? 'border-error-500' : ''}
              />
              {errors.cnpj && (
                <p className="mt-1 text-sm text-error-600">{errors.cnpj.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="endereco">Endereço</Label>
              <Input
                id="endereco"
                placeholder="Ex: Rua das Flores, 123"
                {...register('endereco')}
                className={errors.endereco ? 'border-error-500' : ''}
              />
              {errors.endereco && (
                <p className="mt-1 text-sm text-error-600">{errors.endereco.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="telefone">Telefone</Label>
              <Input
                id="telefone"
                placeholder="Ex: (11) 1234-5678"
                {...register('telefone')}
                className={errors.telefone ? 'border-error-500' : ''}
              />
              {errors.telefone && (
                <p className="mt-1 text-sm text-error-600">{errors.telefone.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Ex: contato@instituicao.edu.br"
                {...register('email')}
                className={errors.email ? 'border-error-500' : ''}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="nomeDiretor">Nome do Diretor</Label>
              <Input
                id="nomeDiretor"
                placeholder="Ex: João Silva"
                {...register('nomeDiretor')}
                className={errors.nomeDiretor ? 'border-error-500' : ''}
              />
              {errors.nomeDiretor && (
                <p className="mt-1 text-sm text-error-600">{errors.nomeDiretor.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="cpfDiretor">CPF do Diretor</Label>
              <Input
                id="cpfDiretor"
                placeholder="Ex: 123.456.789-00"
                {...register('cpfDiretor')}
                className={errors.cpfDiretor ? 'border-error-500' : ''}
              />
              {errors.cpfDiretor && (
                <p className="mt-1 text-sm text-error-600">{errors.cpfDiretor.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/instituicoes')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || loadingInstituicao || loadingCidades}
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Instituição' : 'Criar Instituição')}
          </Button>
        </div>
      </form>
    </div>
  );
};
